import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordIssue = useMemo(() => {
    if (!newPassword) return null;
    if (newPassword.length < 8) return 'Password must be at least 8 characters';
    if (confirmPassword && newPassword !== confirmPassword) return "Passwords don't match";
    return null;
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing reset token. Please click the link in your email again.'); return; }
    if (passwordIssue) { setError(passwordIssue); return; }
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/admin/login'), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E5E7EB] p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/5fygylhw_IMG_0281.jpeg"
                alt="Novena Foundation"
                className="w-16 h-16 mx-auto"
              />
            </Link>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">
              Set a New Password
            </h1>
            <p className="text-sm text-[#4B5563] mt-2">
              Choose a strong password to secure your admin account.
            </p>
          </div>

          {!token && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-6 flex items-center gap-2" data-testid="reset-password-missing-token">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">Missing or invalid reset link. Please request a new one.</span>
            </div>
          )}

          {success ? (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-4 rounded flex items-start gap-2" data-testid="reset-password-success">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold mb-1">Password reset successful!</p>
                <p>Redirecting you to the sign-in page…</p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2" data-testid="reset-password-error">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#4B5563] mb-1">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      data-testid="reset-password-new-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#0A0A0A]"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#4B5563] mb-1">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                      placeholder="Type it again"
                      required
                      minLength={8}
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                  {passwordIssue && (
                    <p className="text-[11px] text-amber-600 mt-1" data-testid="reset-password-hint">{passwordIssue}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !token || !!passwordIssue}
                  className="w-full btn-primary py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-password-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="text-center mt-6">
            <Link to="/admin/login" className="text-sm text-[#4B5563] hover:text-[#BA1826]">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
